import { createContext, useContext, useState, useEffect } from 'react'
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, deleteDoc, getDocs, getDoc, where } from 'firebase/firestore'
import { db } from '../services/firebase'
import { AuthContext } from './AuthContext'
import emailService from '../services/emailService'
import analyticsService from '../services/analyticsService'
import notificationService from '../services/notificationService'
import subscriptionService from '../services/subscriptionService'
import toast from 'react-hot-toast'

const AlertContext = createContext()

export function useAlerts() {
  return useContext(AlertContext)
}

export function AlertProvider({ children }) {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Safely get auth context
  const authContext = useContext(AuthContext)
  const currentUser = authContext?.currentUser
  const userProfile = authContext?.userProfile

  useEffect(() => {
    if (!currentUser || !userProfile) {
      setAlerts([])
      setLoading(false)
      return
    }

    // Get organization ID with fallback logic
    let orgId = userProfile?.organizationId
    if (!orgId && userProfile?.organizations && userProfile.organizations.length > 0) {
      orgId = userProfile.organizations[0] // organizations is array of strings
    }

    // Ensure orgId is a string
    if (orgId && typeof orgId !== 'string') {
      orgId = String(orgId)
    }

    if (!orgId) {
      setAlerts([])
      setLoading(false)
      return
    }

    // Listen to alerts from the user's organization
    const unsubscribe = onSnapshot(
      query(collection(db, 'organizations', orgId, 'alerts'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const alertsData = snapshot.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            ...data
          }
        })
        setAlerts(alertsData)
        setLoading(false)
      },
      (error) => {
        console.error('❌ AlertContext: Error listening to alerts:', error)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [currentUser, userProfile])

  // Check if platform admin should be notified of this alert
  const shouldNotifyPlatformAdmin = async (alertData, organizationId) => {
    try {
      // Only notify platform admin if they follow this organization
      const adminEmail = 'jed@chimeo.app'
      const usersQuery = query(collection(db, 'users'), where('email', '==', adminEmail))
      const usersSnapshot = await getDocs(usersQuery)
      
      if (!usersSnapshot.empty) {
        const adminDoc = usersSnapshot.docs[0]
        const adminData = adminDoc.data()
        const followedOrganizations = adminData.followedOrganizations || []
        
        if (followedOrganizations.includes(organizationId)) {
          console.log('📧 Platform admin follows this organization - sending notification')
          return true
        } else {
          console.log('📧 Platform admin does not follow this organization - skipping notification')
          return false
        }
      } else {
        console.log('📧 Platform admin user document not found - skipping notification')
        return false
      }
      
    } catch (error) {
      console.error('❌ Error checking if should notify platform admin:', error)
      // Default to not notifying on error to avoid spam
      return false
    }
  }

  const createAlert = async (alertData) => {
    try {
      if (!currentUser || !userProfile) {
        throw new Error('User not authenticated or profile not loaded')
      }


      // Refresh user profile to ensure we have the latest organization information
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
        if (userDoc.exists()) {
          const freshProfile = userDoc.data()
          
          // Update local userProfile if it has new organization info
          if (freshProfile.organizationId && freshProfile.organizationName) {
            // Update local userProfile with fresh organization info
          }
        }
      } catch (error) {
        console.warn('Could not refresh user profile:', error)
      }

      // Get user's organization info - we need to find which organization they're an admin of
      let organizationId = null
      let organizationName = null
      
      
      // Check if user is an admin of any organization
      if (userProfile.isOrganizationAdmin) {
        // Try to get the organization ID from userProfile or find it in organizations
        organizationId = userProfile.organizationId
        organizationName = userProfile.organizationName
      } else {
      }

      // If we don't have organization info, try to get it from adminService
      if (!organizationId) {
        try {
          const adminService = (await import('../services/adminService')).default
          adminService.setCurrentUser(currentUser)
          
          const hasAdminAccess = await adminService.hasOrganizationAdminAccess()
          if (hasAdminAccess) {
            const adminOrgs = await adminService.getAdminOrganizations()
            if (adminOrgs.length > 0) {
              const primaryOrg = adminOrgs[0]
              organizationId = primaryOrg.id
              organizationName = primaryOrg.name
            }
          }
        } catch (error) {
          console.warn('Could not get organization info from adminService:', error)
        }
      }

      // If we still don't have organization info, we can't send phone notifications
      if (!organizationId) {
        console.error('❌ No organization ID found for alert creation')
        console.error('❌ User profile:', userProfile)
        console.error('❌ Current user:', currentUser)
        console.error('❌ Alert data:', alertData)
        throw new Error('You must be an organization administrator to create alerts that send phone notifications. Please check your profile to ensure you have admin access.')
      }
      
      console.log('✅ Organization info found:', { organizationId, organizationName })

      // Create alert in the mobile app structure for cloud function compatibility
      const alertPayload = {
        title: alertData.title,
        description: alertData.message,
        organizationId: organizationId,
        organizationName: organizationName,
        type: alertData.type,
        severity: alertData.severity || (alertData.type === 'emergency' ? 'high' : alertData.type === 'warning' ? 'medium' : 'low'),
        postedBy: userProfile.creatorName || userProfile.displayName || currentUser.email,
        postedByUserId: currentUser.uid,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        location: alertData.location || '',
        groupId: alertData.groupId || null, // Add group targeting
        // Web app specific fields
        source: 'web',
        webAlertData: alertData
      }

      console.log('🔧 Creating alert in mobile app structure:', {
        organizationId,
        organizationName,
        alertPayload
      })

      // Store in the organization's alerts subcollection (for web app display)
      const webAlertRef = await addDoc(collection(db, 'organizations', organizationId, 'alerts'), alertPayload)
      console.log('✅ Alert created in organization alerts subcollection:', webAlertRef.id)
      console.log('✅ Alert payload for web app:', alertPayload)

      // Track analytics event
      await analyticsService.trackAlertCreated({
        id: webAlertRef.id,
        type: alertData.type,
        severity: alertPayload.severity,
        groupId: alertData.groupId,
        location: alertData.location,
        postedByUserId: currentUser.uid
      }, organizationId)

      // Increment usage counter for subscription tracking
      try {
        const subscriptionService = (await import('../services/subscriptionService')).default
        await subscriptionService.incrementUsage(currentUser.uid, 'alerts', organizationId)
      } catch (usageError) {
        console.warn('⚠️ AlertContext: Could not increment usage counter:', usageError.message)
      }

      // Prepare notification payload for both push and email notifications
      const notificationPayload = {
        id: webAlertRef.id,
        title: alertData.title,
        message: alertData.message,
        organizationId: organizationId,
        organizationName: organizationName || 'Unknown Organization',
        createdBy: currentUser?.displayName || currentUser?.email || 'Unknown User',
        groupName: alertData.groupName || 'All Groups',
        location: alertData.location || 'Not specified',
        type: alertData.type || 'general',
        priority: alertData.priority || 'Normal'
      }

      // Send push notification to users
      try {
        console.log('🔔 Sending push notification for alert...')
        console.log('🔔 Notification payload:', notificationPayload)
        console.log('🔔 Notification service:', notificationService)
        await notificationService.sendAlertNotification(notificationPayload)
        console.log('✅ Push notification sent successfully')
      } catch (notificationError) {
        console.error('❌ Failed to send push notification:', notificationError)
        console.error('❌ Notification error details:', notificationError.message)
        console.error('❌ Notification error stack:', notificationError.stack)
        // Don't fail the alert creation if notification fails
      }

      // Also try to send via Firebase Function for better reliability
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions')
        const { functions } = await import('../services/firebase')
        
        if (functions) {
          // Use group-specific notification if it's a group alert, otherwise use organization-wide
          if (alertData.groupId && alertData.groupName) {
            const sendGroupPushNotification = httpsCallable(functions, 'sendGroupPushNotification')
            
            await sendGroupPushNotification({
              organizationId: organizationId,
              groupId: alertData.groupId,
              groupName: alertData.groupName,
              title: notificationPayload.title,
              body: notificationPayload.message,
              data: {
                type: 'alert',
                alertId: webAlertRef.id,
                organizationId: organizationId,
                groupId: alertData.groupId,
                groupName: alertData.groupName
              }
            })
            
            console.log('✅ Group push notification sent via Firebase Function')
          } else {
            const sendOrganizationPushNotification = httpsCallable(functions, 'sendOrganizationPushNotification')
            
            await sendOrganizationPushNotification({
              organizationId: organizationId,
              title: notificationPayload.title,
              body: notificationPayload.message,
              data: {
                type: 'alert',
                alertId: webAlertRef.id,
                organizationId: organizationId
              }
            })
            
            console.log('✅ Organization push notification sent via Firebase Function')
          }
        }
      } catch (functionError) {
        console.warn('⚠️ Firebase Function push notification failed:', functionError.message)
      }

      // Check if email alerts are allowed for this subscription
      const subscription = await subscriptionService.getUserSubscription(currentUser.uid)
      console.log('📧 User subscription:', subscription)
      const emailAlertsAllowed = subscriptionService.areEmailAlertsAllowed(subscription)
      console.log('📧 Email alerts allowed:', emailAlertsAllowed)
      
      if (!emailAlertsAllowed) {
        console.log('📧 Email alerts not allowed for free tier - skipping email notifications')
      } else {
        console.log('📧 Email alerts are allowed - proceeding with email notifications')
        // Send email notifications to group followers OR organization followers
        try {
          if (alertData.groupId) {
            console.log('📧 Sending alert emails to group followers...')
            console.log(`📧 Group ID from alert: ${alertData.groupId}`)
            
            // Get group followers from groups/{groupId}/followers collection
            const groupFollowersQuery = query(
              collection(db, 'organizations', organizationId, 'groups', alertData.groupId, 'followers'),
              where('isActive', '==', true)
            )
            const groupFollowersSnapshot = await getDocs(groupFollowersQuery)
            
            console.log(`📧 Found ${groupFollowersSnapshot.docs.length} group followers in collection`)
            
            const groupFollowers = []
            for (const followerDoc of groupFollowersSnapshot.docs) {
              const followerData = followerDoc.data()
              if (followerData.userEmail) {
                groupFollowers.push({
                  id: followerData.userId,
                  email: followerData.userEmail,
                  displayName: followerData.userName || followerData.userEmail
                })
                console.log(`📧 Found group follower: ${followerData.userEmail}`)
              }
            }
            
            console.log(`📧 Total group followers to notify: ${groupFollowers.length}`)
            console.log('📧 Group followers:', groupFollowers.map(f => ({ email: f.email, displayName: f.displayName })))
            
            // Send email to each group follower
            for (const follower of groupFollowers) {
              console.log(`📧 Processing group follower: ${follower.email}`)
              if (follower.email && follower.email !== currentUser.email) {
                console.log(`📧 Group follower ${follower.email} is valid for email sending`)
                // Check if user has opted in to group emails
                const notificationPrefs = follower.notificationPreferences || {
                  emailNotifications: true,
                  groupEmails: true
                }
                
                console.log(`📧 Notification prefs for ${follower.email}:`, notificationPrefs)
                
                if (notificationPrefs.emailNotifications && notificationPrefs.groupEmails) {
                  try {
                    console.log(`📧 Attempting to send email to ${follower.email}...`)
                    console.log(`📧 Notification payload:`, notificationPayload)
                    console.log(`📧 Organization ID:`, organizationId)
                    const emailResult = await emailService.sendAlertEmail(notificationPayload, follower.email, organizationId)
                    console.log(`✅ Alert email sent to group follower: ${follower.email}`, emailResult)
                  } catch (memberEmailError) {
                    console.error(`❌ Failed to send alert email to ${follower.email}:`, memberEmailError)
                  }
                } else {
                  console.log(`📵 User ${follower.email} has disabled group email notifications`)
                }
              } else {
                console.log(`📵 Skipping ${follower.email} - no email or is current user`)
              }
            }
          } else {
            console.log('📧 No specific group targeted, sending emails to organization followers...')
            
            // Get organization followers for organization-wide alerts
            const orgFollowersQuery = query(
              collection(db, 'organizations', organizationId, 'followers'),
              where('isActive', '==', true)
            )
            const orgFollowersSnapshot = await getDocs(orgFollowersQuery)
            
            const orgFollowers = []
            for (const followerDoc of orgFollowersSnapshot.docs) {
              const followerData = followerDoc.data()
              if (followerData.userEmail) {
                orgFollowers.push({
                  id: followerData.userId,
                  email: followerData.userEmail,
                  displayName: followerData.userName || followerData.userEmail
                })
              }
            }
            
            console.log(`📧 Found ${orgFollowers.length} organization followers to notify`)
            console.log('📧 Organization followers:', orgFollowers.map(f => ({ email: f.email, displayName: f.displayName })))
            
            // Send email to each organization follower
            for (const follower of orgFollowers) {
              console.log(`📧 Processing organization follower: ${follower.email}`)
              if (follower.email && follower.email !== currentUser.email) {
                console.log(`📧 Organization follower ${follower.email} is valid for email sending`)
                // Check if user has opted in to organization emails
                const notificationPrefs = follower.notificationPreferences || {
                  emailNotifications: true,
                  organizationEmails: true
                }
                
                console.log(`📧 Notification prefs for ${follower.email}:`, notificationPrefs)
                
                if (notificationPrefs.emailNotifications && notificationPrefs.organizationEmails) {
                  try {
                    console.log(`📧 Attempting to send email to ${follower.email}...`)
                    console.log(`📧 Notification payload:`, notificationPayload)
                    console.log(`📧 Organization ID:`, organizationId)
                    const emailResult = await emailService.sendAlertEmail(notificationPayload, follower.email, organizationId)
                    console.log(`✅ Alert email sent to organization follower: ${follower.email}`, emailResult)
                  } catch (memberEmailError) {
                    console.error(`❌ Failed to send alert email to ${follower.email}:`, memberEmailError)
                  }
                } else {
                  console.log(`📵 User ${follower.email} has disabled organization email notifications`)
                }
              } else {
                console.log(`📵 Skipping ${follower.email} - no email or is current user`)
              }
            }
          }
        } catch (emailError) {
          console.error('❌ Failed to send alert email notifications:', emailError)
          // Don't fail the alert creation if emails fail
        }
      }

      toast.success('Alert created successfully! Push notifications sent to web users.')
      console.log('🎉 Alert creation complete!')
      return webAlertRef
    } catch (error) {
      console.error('Error creating alert:', error)
      toast.error(error.message || 'Failed to create alert')
      throw error
    }
  }

  const updateAlert = async (alertId, updates) => {
    try {
      // Get organization ID with fallback logic
      let orgId = userProfile?.organizationId
      if (!orgId && userProfile?.organizations && userProfile.organizations.length > 0) {
        orgId = userProfile.organizations[0] // organizations is array of strings
      }

      if (!orgId) {
        throw new Error('No organization ID found for updating alert')
      }

      await updateDoc(doc(db, 'organizations', orgId, 'alerts', alertId), {
        ...updates,
        updatedAt: new Date()
      })
      toast.success('Alert updated successfully!')
    } catch (error) {
      console.error('Error updating alert:', error)
      toast.error('Failed to update alert')
      throw error
    }
  }

  const deleteAlert = async (alertId) => {
    try {
      console.log('🗑️ Deleting alert:', alertId)
      
      // Get organization ID with fallback logic
      let orgId = userProfile?.organizationId
      if (!orgId && userProfile?.organizations && userProfile.organizations.length > 0) {
        orgId = userProfile.organizations[0] // organizations is array of strings
      }

      if (!orgId) {
        throw new Error('No organization ID found for deleting alert')
      }
      
      console.log('🗑️ Organization ID:', orgId)
      
      // Delete from the organization's alerts subcollection
      const deletePromises = [
        deleteDoc(doc(db, 'organizations', orgId, 'alerts', alertId))
      ]
      
      // Execute all deletions
      await Promise.all(deletePromises)
      
      console.log('✅ Alert deleted successfully from all collections')
      toast.success('Alert deleted successfully!')
    } catch (error) {
      console.error('Error deleting alert:', error)
      toast.error('Failed to delete alert')
      throw error
    }
  }

  const value = {
    alerts,
    loading,
    createAlert,
    updateAlert,
    deleteAlert
  }

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  )
}
